import { useNavigate } from 'react-router-dom';

import { AppIcon, BACKRS_ROLES, BadgeCircle, UploadedFile } from '@backrs_ui/common-ui';

import { getMarkdownComponents } from '@backrs_ui/common-ui/src/molecules/Markdown/markdownComponents';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ProfileBanner } from '@backrs_ui/grow-ui/src/components/ProfileBanner/ProfileBanner';
import { PublicProfileSection } from '@backrs_ui/grow-ui/src/pages/Profile/atoms/PublicProfileSection';
import { SnapshotEnrollmentSection } from '@backrs_ui/grow-ui/src/pages/Profile/atoms/SnapshotEnrollmentSection';
import { useGetSelectedOptions } from '@backrs_ui/grow-ui/src/pages/Profile/hooks/useGetSelectedOptions';
import {
  Assignee,
  InstitutionMember,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import {
  useFetchProfileValueOptionsQuery,
  ProfileFields,
  ProfileValueOption,
  UserProfileResponse,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { personInitial, personName } from '@backrs_ui/grow-ui/src/utils/personName';

import { Avatar, Box, Chip, type SxProps, type Theme, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { AssessmentStatusSection } from './AssessmentStatusSection';
import { MemberCoachesSection } from './MemberCoachesSection';
import { MemberDetailActionButtons } from './MemberDetailActionButtons';
import { SolutionSessionsSection } from './SolutionSessionsSection';

const PUBLIC_PROFILE_STRINGS = appStrings.modules.profile.public;

type MemberProfileInfoCardProps = Readonly<{
  member: Assignee | InstitutionMember;
  // Optional - if omitted, summary section is not rendered
  profileSummary?: string;
  isSummaryLoading?: boolean;
  summaryError?: boolean;
  canViewCoaches?: boolean;
  canManageCoaches?: boolean;
}>;

export const getMemberRole = (member: Assignee | InstitutionMember): string | null => {
  const roleNames = member.roles?.map((role) => role.name) ?? [];

  if (roleNames.includes(BACKRS_ROLES.SCHOLAR)) return BACKRS_ROLES.SCHOLAR;
  if (roleNames.includes(BACKRS_ROLES.STUDENT)) return BACKRS_ROLES.STUDENT;

  return null;
};

export const MemberProfileInfoCard = ({
  member,
  profileSummary,
  isSummaryLoading,
  summaryError,
  canViewCoaches = false,
  canManageCoaches = false,
}: MemberProfileInfoCardProps) => {
  const navigate = useNavigate();

  const { data: profileValueOptions } = useFetchProfileValueOptionsQuery(undefined);

  const selectedOptions = useGetSelectedOptions({
    userProfile: member as UserProfileResponse,
    isEditView: false,
  });

  const findAllStringValues = (
    fieldKey: ProfileFields,
  ): { name: string; category: ProfileFields }[] => {
    const filteredOptions = selectedOptions(fieldKey).filter(
      (opt): opt is ProfileValueOption | string => opt !== undefined,
    );

    return filteredOptions.map((opt) => {
      if (typeof opt === 'object' && 'name' in opt) {
        return { name: opt.name, category: fieldKey };
      }

      return { name: opt?.toString() || '', category: fieldKey };
    });
  };

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <ProfileBanner
          id="member-profile-banner"
          bannerImageUrl={member?.profile?.publicProfile?.profile_banner_image}
          seed={member?.profile?.publicProfile?.id ?? member.id}
          sx={backgroundHeaderImageSx}
        />
        <Box sx={bannerActionsSx}>
          <MemberDetailActionButtons member={member} />
        </Box>
        <Box sx={headerInfoSx}>
          <Box sx={avatarNameSx}>
            <Avatar
              src={member.profile_image ?? undefined}
              alt={personName(member)}
              sx={profileAvatarSx}
            >
              {personInitial(member)}
            </Avatar>
            <Box sx={nameGradeSx}>
              <Typography variant="display-xs-semibold" sx={nameSx}>
                {personName(member)}
              </Typography>
              {member.grade ? (
                <Typography variant="text-sm-regular" sx={gradeSx}>
                  {member.grade}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <Box sx={contactInfoSx}>
            {member.email && (
              <Box sx={contactRowSx}>
                <AppIcon iconName="Mail01" diameter="16px" color="Colors/Foreground/fg-tertiary" />
                <Typography variant="text-sm-regular" sx={contactTextSx}>
                  {member.email}
                </Typography>
              </Box>
            )}
            {member.phone && (
              <Box sx={contactRowSx}>
                <AppIcon iconName="Phone" diameter="16px" color="Colors/Foreground/fg-tertiary" />
                <Typography variant="text-sm-regular" sx={contactTextSx}>
                  {member.phone}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={gridContainerSx}>
          <Box sx={profileInfoSx}>
            <Box sx={flexColumnSx}>
              {member?.school ? (
                <SnapshotEnrollmentSection
                  publicProfile={member as UserProfileResponse}
                  profileValueOptions={profileValueOptions}
                  findAllStringValues={findAllStringValues}
                />
              ) : null}
              {member?.institutions?.length ? (
                <PublicProfileSection
                  title={PUBLIC_PROFILE_STRINGS['sponsoringInstitution']}
                  variant={{
                    type: 'CHILDREN',
                    children: (
                      <Box>
                        {member.institutions.map((institution) => (
                          <Chip
                            key={institution?.id}
                            label={institution?.name}
                            variant="tag"
                            size="md"
                            sx={institutionChipSx}
                          />
                        ))}
                      </Box>
                    ),
                  }}
                />
              ) : null}
              {canViewCoaches ? (
                <MemberCoachesSection
                  coaches={member.coaches}
                  memberId={member.id}
                  memberName={personName(member)}
                  canManageCoaches={canManageCoaches}
                />
              ) : null}
            </Box>
            <Box sx={flexColumnSx}>
              {member?.assessments ? (
                <AssessmentStatusSection assessments={member.assessments} />
              ) : null}
              <SolutionSessionsSection memberId={member.id} />
            </Box>
            <Box sx={documentsColumnSx}>
              {member?.profile?.documentsResumes?.length ? (
                <PublicProfileSection
                  title={PUBLIC_PROFILE_STRINGS.resume}
                  variant={{
                    type: 'CHILDREN',
                    children: (
                      <Box
                        sx={uploadedFileContainerSx}
                        onClick={() =>
                          window.open(member?.profile?.documentsResumes?.[0]?.media_url, '_blank')
                        }
                      >
                        <UploadedFile
                          fileName={member?.profile?.documentsResumes?.[0]?.title ?? ''}
                          fileSize={member?.profile?.documentsResumes?.[0]?.description ?? ''}
                          fileType={member?.profile?.documentsResumes?.[0]?.media_type ?? ''}
                          borderColor="dark"
                        />
                      </Box>
                    ),
                  }}
                />
              ) : null}
              {member?.profile?.documentsTranscripts?.length ? (
                <PublicProfileSection
                  title="Transcript"
                  variant={{
                    type: 'CHILDREN',
                    children: (
                      <Box
                        sx={uploadedFileContainerSx}
                        onClick={() =>
                          window.open(
                            member?.profile?.documentsTranscripts?.[0]?.media_url,
                            '_blank',
                          )
                        }
                      >
                        <UploadedFile
                          fileName={member?.profile?.documentsTranscripts?.[0]?.title ?? ''}
                          fileSize={member?.profile?.documentsTranscripts?.[0]?.description ?? ''}
                          fileType={member?.profile?.documentsTranscripts?.[0]?.media_type ?? ''}
                          borderColor="dark"
                        />
                      </Box>
                    ),
                  }}
                />
              ) : null}
            </Box>
          </Box>
          {/* AI Profile Summary - only rendered when profileSummary prop is provided */}
          {profileSummary !== undefined && (
            <Box sx={aiSummaryContainerSx}>
              <Chip
                label="Lubav Summary"
                variant="tag"
                size="md"
                icon={<BadgeCircle loading={isSummaryLoading} size="md" />}
                sx={institutionChipSx}
              />
              <Typography variant="text-sm-regular" sx={aiSummaryTextSx}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={getMarkdownComponents('SMALL', navigate)}
                >
                  {profileSummary}
                </ReactMarkdown>
              </Typography>
              {summaryError && (
                <Typography variant="text-sm-regular" color="error" sx={{ mt: 1 }}>
                  Failed to generate profile summary
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

const gridContainerSx: SxProps<Theme> = () => ({
  marginTop: '2rem',
});

const profileAvatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: 116,
  height: 116,
  fontSize: '2rem',
  marginTop: '-64px',
  border: `2px solid ${backrs.colors['Beige/950']}`,
});

const headerInfoSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  justifyContent: 'space-between',
  gap: { xs: backrs.spacing['spacing-md'], sm: 0 },
  marginTop: '1rem',
  marginInline: '0.25rem',
});

const avatarNameSx: SxProps<Theme> = () => ({
  display: 'flex',
  gap: '1.5rem',
  marginLeft: '1.5rem',
});

const nameGradeSx: SxProps<Theme> = () => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
});

const nameSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const gradeSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Beige/950'],
});

const uploadedFileContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const backgroundHeaderImageSx: SxProps<Theme> = () => ({
  height: '144px',
});

const bannerActionsSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-lg'],
  right: backrs.spacing['spacing-lg'],
  zIndex: 1,
});

const institutionChipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  width: 'fit-content',
  mb: backrs.spacing['spacing-xs'],
});

const aiSummaryContainerSx: SxProps<Theme> = ({ backrs }) => ({
  borderRadius: backrs.radius['radius-md'],
  borderTopLeftRadius: 0,
  padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-xl']}`,
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${backrs.colors['Beige/950']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  gap: backrs.spacing['spacing-sm'],
  marginTop: backrs.spacing['spacing-4xl'],
  marginBottom: backrs.spacing['spacing-xl'],
});

const aiSummaryTextSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-2xl'],
  '& p': {
    margin: backrs.spacing['spacing-none'],
    padding: backrs.spacing['spacing-none'],
  },
  '& p:not(:last-child)': {
    marginBottom: backrs.spacing['spacing-md'],
  },
  '& ul, & ol': {
    margin: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-none']}`,
    paddingLeft: backrs.spacing['spacing-3xl'],
  },
  '& li': {
    marginBottom: backrs.spacing['spacing-xs'],
  },
  '& em, & i': {
    fontStyle: 'italic',
  },
});

const contactInfoSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  alignItems: { xs: 'flex-start', sm: 'flex-end' },
  marginLeft: { xs: backrs.spacing['spacing-3xl'], sm: 0 },
  marginRight: { xs: 0, sm: backrs.spacing['spacing-md'] },
});

const contactRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const contactTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const profileInfoSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  columnGap: { xs: 0, sm: backrs.spacing['spacing-xl'] },
  rowGap: { xs: backrs.spacing['spacing-lg'], sm: 0 },
});

const flexColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  flex: '1 1 0px',
  minWidth: 0,
});

const documentsColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  flex: '1 1 0px',
  minWidth: 0,
});
